import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';
import type { InnerCircleContact } from '../types/inner-circle';

export function useInnerCircle() {
  const { user } = useAuthContext();
  const [contacts, setContacts] = useState<InnerCircleContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (user) {
      fetchContacts();
      
      const subscription = supabase
        .channel('inner_circle_changes')
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'inner_circle' },
          fetchContacts
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user]);

const fetchContacts = async () => {
  if (!user) return;
  
  try {
    // First get all contacts
    const { data: contactsData, error: contactsError } = await supabase
      .from('inner_circle')
      .select('*')
      .or(`user_id.eq.${user.id},contact_id.eq.${user.id}`);

    if (contactsError) throw contactsError;

    // Then get user data for each contact
    const contactsWithProfiles = await Promise.all(
      contactsData.map(async (contact) => {
        // Determine which ID we need (if you're the user_id, we need contact_id's info and vice versa)
        const profileId = contact.user_id === user.id ? contact.contact_id : contact.user_id;
        
        const { data: userData, error: userError } = await supabase
          .from('auth.users')
          .select('id, email, raw_user_meta_data')
          .eq('id', profileId)
          .single();

        if (userError) throw userError;

        return {
          ...contact,
          created_at: new Date(contact.created_at),
          updated_at: new Date(contact.updated_at),
          profile: {
            id: userData.id,
            email: userData.email,
            name: userData.raw_user_meta_data?.name,
            avatar_url: userData.raw_user_meta_data?.avatar_url
          }
        };
      })
    );

    setContacts(contactsWithProfiles);
  } catch (err) {
    console.error('Error fetching inner circle contacts:', err);
    setError(err as Error);
  } finally {
    setLoading(false);
  }
};

const addContact = async (email: string, notes?: string) => {
  try {
    // First find the user by email
    const { data: userData, error: userError } = await supabase
      .from('auth.users')
      .select('id')
      .eq('email', email)
      .single();

    if (userError || !userData) throw new Error('User not found');

    const { error } = await supabase
      .from('inner_circle')
      .insert({
        user_id: user?.id,
        contact_id: userData.id,
        status: 'pending',
        notes
      });

    if (error) throw error;
    await fetchContacts();
  } catch (err) {
    console.error('Error adding contact:', err);
    throw err;
  }
};

  const updateContactStatus = async (contactId: string, status: InnerCircleContact['status']) => {
    try {
      const { error } = await supabase
        .from('inner_circle')
        .update({ status })
        .eq('id', contactId)
        .eq('user_id', user?.id);

      if (error) throw error;
      await fetchContacts();
    } catch (err) {
      console.error('Error updating contact status:', err);
      throw err;
    }
  };

  return {
    contacts,
    loading,
    error,
    addContact,
    updateContactStatus
  };
}